import { Injectable } from '@angular/core';
import {
    FlyFreelyError,
    FlyFreelyLoggingService,
    MissionCrewDto,
    MissionDetailsDto,
    OrganisationAuthorityService,
    PersonRolesDto,
    PersonService,
    WorkTracker,
    toTimestamp
} from '@flyfreely-portal-ui/flyfreely';
import {
    NgSelectPaginationValues,
    NgSelectSearch,
    ngSelectPreSearchConfigure,
    updateNgSelectData
} from '@flyfreely-portal-ui/ng-select';
import { personSearch } from '@flyfreely-portal-ui/ui';
import {
    BehaviorSubject,
    Observable,
    Subject,
    combineLatest,
    concatMap,
    debounceTime,
    map,
    of,
    takeUntil
} from 'rxjs';
import { PersonWithRegisterStatus } from '../mission-edit-v2/mission-edit.service';

@Injectable()
export class MissionPilotRoleDataService {
    organisationId: number;
    excludedIds: number[];
    roleFilter: PersonRolesDto.Roles;
    missionDetails: MissionDetailsDto;

    personSearch = personSearch.bind(this);

    private personnelSource = new BehaviorSubject<PersonWithRegisterStatus[]>(
        []
    );
    personnel$ = this.personnelSource.asObservable();

    private personSearchSource = new Subject<NgSelectSearch>();
    personSelectConfig: NgSelectPaginationValues = {
        currentPage: 0,
        preSearchPage: null,
        searchValue: null,
        totalItems: null
    };

    private authorityIdsSource = new BehaviorSubject<number[]>(null);
    authorityIds$ = this.authorityIdsSource.asObservable();

    private workTracker = new WorkTracker();
    private workingSource = new BehaviorSubject<boolean>(false);
    working$ = this.workingSource.asObservable();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private personService: PersonService,
        private organisationAuthorityService: OrganisationAuthorityService,
        private logging: FlyFreelyLoggingService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => this.workingSource.next(working));

        this.personSearchSource
            .pipe(debounceTime(300), takeUntil(this.ngUnsubscribe$))
            .subscribe(search => {
                this.personSelectConfig = ngSelectPreSearchConfigure(
                    search,
                    this.personSelectConfig
                );
                this.refreshPersonList();
            });
    }

    ngOnDestroy() {
        this.personSearchSource.complete();
        this.personnelSource.complete();
        this.authorityIdsSource.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    setParameters(
        organisationId: number,
        role: PersonRolesDto.Roles,
        excludeIds: number[],
        missionDetails: MissionDetailsDto,
        existingPersonId$: Subject<MissionCrewDto>
    ) {
        this.organisationId = organisationId;
        this.roleFilter = role;
        this.excludedIds = excludeIds;
        this.missionDetails = missionDetails;
        this.findMissionAuthorities();
        this.resetPersonSelectConfig();
        existingPersonId$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(missionCrew => {
                const existingPersonId = missionCrew?.personId;
                if (existingPersonId != null) {
                    this.findExistingPerson(existingPersonId);
                }
            });
        this.refreshPersonList();
    }

    private findMissionAuthorities() {
        if (this.missionDetails == null) {
            this.authorityIdsSource.next(null);
        } else {
            const workflowAuthorities =
                this.missionDetails.missionWorkflowVersion
                    ?.delegatedAuthority != null
                    ? [
                          this.missionDetails.missionWorkflowVersion
                              .delegatedAuthorityId
                      ].concat(
                          this.missionDetails.missionWorkflowVersion
                              .additionalAuthorityIds ?? []
                      )
                    : this.missionDetails.missionWorkflowVersion
                          ?.additionalAuthorities != null
                    ? this.missionDetails.missionWorkflowVersion
                          .additionalAuthorityIds
                    : [];

            const additionalAuthorities =
                this.missionDetails.additionalAuthorities?.map(a => a.id) ?? [];

            this.authorityIdsSource.next(
                workflowAuthorities.concat(additionalAuthorities)
            );
        }
    }

    private findExistingPerson(personId: number) {
        if (this.personnel.find(p => p.id === personId) != null) {
            return;
        }

        this.personService
            .findPersonnelGraphQL(this.organisationId, 0, 1, {
                idList: [personId]
            })
            .pipe(
                concatMap(result => {
                    const newPersonnel: PersonWithRegisterStatus[] =
                        result.results
                            ?.map<PersonWithRegisterStatus>(
                                p =>
                                    ({
                                        id: p.id,
                                        firstName: p.firstName,
                                        lastName: p.lastName,
                                        email: p.email,
                                        phoneNumber: p.phoneNumber,
                                        roles: p.roles,
                                        workgroups: p.workgroupList,
                                        active: p.active,
                                        registerStatuses: null
                                    } as any)
                            )
                            .filter(p => p != null && p.id != null) ?? [];

                    const authorityIds = this.authorityIdsSource.getValue();
                    if (
                        authorityIds != null &&
                        authorityIds.length > 0 &&
                        newPersonnel.length > 0
                    ) {
                        return this.findAuthorityIds(newPersonnel);
                    } else {
                        return of(newPersonnel);
                    }
                }),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: (newPersonnel: PersonWithRegisterStatus[]) => {
                    const updatedPersonnel =
                        this.personnel.concat(newPersonnel);

                    this.personnelSource.next(updatedPersonnel);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading mission personnel: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    private refreshPersonList() {
        this.personService
            .findPersonnelGraphQL(
                this.organisationId,
                this.personSelectConfig.currentPage,
                15,
                {
                    role: this.roleFilter,
                    textSearch:
                        this.personSelectConfig.searchValue || undefined,
                    excludedIdList: this.excludedIds
                }
            )
            .pipe(
                concatMap(result => {
                    const newPersonnel: PersonWithRegisterStatus[] =
                        result.results
                            ?.filter(
                                p =>
                                    p != null &&
                                    this.personnel.findIndex(
                                        person => person.id === p.id
                                    ) === -1
                            )
                            .map<PersonWithRegisterStatus>(
                                p =>
                                    ({
                                        id: p.id,
                                        firstName: p.firstName,
                                        lastName: p.lastName,
                                        email: p.email,
                                        phoneNumber: p.phoneNumber,
                                        roles: p.roles,
                                        workgroups: p.workgroupList,
                                        active: p.active,
                                        registerStatuses: null
                                    } as any)
                            )
                            .filter(p => p != null && p.id != null) ?? [];
                    this.personSelectConfig = updateNgSelectData(
                        result,
                        this.personSelectConfig
                    );

                    const authorityIds = this.authorityIdsSource.getValue();
                    if (
                        authorityIds != null &&
                        authorityIds.length > 0 &&
                        newPersonnel.length > 0
                    ) {
                        return this.findAuthorityIds(newPersonnel);
                    } else {
                        return of(newPersonnel);
                    }
                }),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: (newPersonnel: PersonWithRegisterStatus[]) => {
                    const updatedPersonnel =
                        this.personnel.concat(newPersonnel);

                    this.personnelSource.next(updatedPersonnel);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading mission personnel: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    private findAuthorityIds(
        newPersonnel: PersonWithRegisterStatus[]
    ): Observable<PersonWithRegisterStatus[]> {
        const personnelIds = newPersonnel.map(p => p.id);
        const authorityIds = this.authorityIdsSource.getValue();
        const time =
            this.missionDetails?.missionDate ?? toTimestamp(new Date());
        return combineLatest(
            authorityIds.map(authId =>
                this.organisationAuthorityService
                    .findPersonnelRegisterStatuses(
                        authId,
                        personnelIds,
                        time,
                        this.missionDetails?.organisationId ??
                            this.organisationId
                    )
                    .pipe(
                        takeUntil(this.ngUnsubscribe$),
                        map(results => ({
                            [authId]: results.reduce(
                                (acc, result) => ({
                                    ...acc,
                                    [result.entityId]: result.registerStatus
                                }),
                                {}
                            )
                        }))
                    )
            )
        ).pipe(
            map(results => {
                const combinedResults = results.reduce((acc, authority) => {
                    const ids = Object.keys(authority);
                    return ids.reduce(
                        (acc2, id) => ({
                            ...acc2,
                            [id]: {
                                ...acc2[id],
                                ...authority[id]
                            }
                        }),
                        {}
                    );
                }, {});
                const authorityIds =
                    combinedResults != null ? Object.keys(combinedResults) : [];
                return newPersonnel.map(p => ({
                    ...p,
                    registerStatuses: authorityIds.reduce(
                        (acc, id) => ({
                            ...acc,
                            [id]:
                                combinedResults[id] != null
                                    ? combinedResults[id][p.id]
                                    : null
                        }),
                        {}
                    )
                }));
            })
        );
    }

    onPersonSelectScroll() {
        const resultLength =
            this.personSelectConfig.searchValue == null
                ? this.personnel.length
                : this.personnel.filter(person =>
                      this.personSearch(
                          this.personSelectConfig.searchValue,
                          person
                      )
                  ).length;

        if (
            this.personSelectConfig.totalItems == null ||
            resultLength !== this.personSelectConfig.totalItems
        ) {
            this.refreshPersonList();
        }
    }

    onPersonSelectSearch(search: NgSelectSearch) {
        this.personSearchSource.next(search);
    }

    onPersonSelectClose() {
        this.resetPersonSelectConfig();
        this.refreshPersonList();
    }

    private resetPersonSelectConfig() {
        this.personSelectConfig = {
            ...this.personSelectConfig,
            searchValue: null,
            currentPage: 0,
            preSearchPage: null
        };
    }

    private get personnel() {
        return this.personnelSource.getValue();
    }
}
